import type { CitationOutput } from "@/components/tool-kit/source-citation-builder";
import {
  type CitationParts,
  familyName,
  invertedName,
  mlaDate,
  naturalName,
  type PartialDate,
  type PersonName,
  parsePartialDate,
  parsePersonName,
  tidyParts,
  withPeriod,
} from "@/lib/tool-kit/citation-parts";
import {
  capitalizeSubtitle,
  closer,
  get,
  italic,
  plain,
  titleCase,
} from "@/lib/tool-kit/legal-sources/common";
import {
  chicagoShortTitle,
  groupName,
  longDate,
  type MediaLabels,
  type MediaValues,
  mlaRoleNames,
  mlaShortTitle,
  parsePeople,
} from "@/lib/tool-kit/media-sources/common";

/*
 * A live performance you attended — a play, musical, opera, ballet or concert — in
 * APA 7, MLA 9 and Chicago 18.
 *
 * Sources checked (October 2026):
 * - APA 7 has no reference format for a performance: a reference must be retrievable,
 *   and an unrecorded live event is not. APA Style, "Personal communications" (§8.9):
 *   "Works that cannot be recovered by readers are cited in the text", with live
 *   speeches and unrecorded webinars among the examples. So the performance is
 *   described in the sentence (what, where, when) with no reference-list entry; a
 *   recording (a filmed stage production, a concert film) is cited as a film or video.
 * - MLA 9: MLA Style Center, "How do I cite a performance I watch in person?" —
 *   "Heartbreak House. By George Bernard Shaw, directed by Robin Lefevre, performances
 *   by Philip Bosco and Swoosie Kurtz, Roundabout Theatre Company, 1 Oct. 2006,
 *   American Airlines Theatre, New York." — and "Song, Recording, or Performance"
 *   (Citations by Format), concert attended in person: "Beyoncé. The “Formation” World
 *   Tour. 14 May 2016, Rose Bowl, Los Angeles." A performer-led entry starts with the
 *   performer; "Concert" stands in for a missing title ("Astley, Rick. Concert. 6 Oct.
 *   2016, Town Hall, New York City."). In text, the entry's first element.
 * - Chicago 18 §14.166, "Live performances" (as summarized by the University of
 *   Illinois and University of Queensland library guides; the Chicago Style Q&A on
 *   live performance points to it): give the title, the creator, key contributors with
 *   their roles, the venue, its city and the date, in the text or a note; a
 *   bibliography entry is generally unnecessary. Example cited from the 18th edition:
 *   "Igor Levit, pianist, Piano Concerto No. 2, by Johannes Brahms, Cleveland
 *   Orchestra, conducted by Franz Welser-Möst, Mandel Concert Hall, Cleveland,
 *   January 6, 2022."
 */

export const LIVE_PERFORMANCE_FIELDS = {
  title: "title",
  creators: "creators",
  creditAs: "creditAs",
  director: "director",
  conductor: "conductor",
  choreographer: "choreographer",
  performers: "performers",
  company: "company",
  performer: "performer",
  performerIsGroup: "performerIsGroup",
  venue: "venue",
  city: "city",
  region: "region",
  date: "date",
} as const;

const F = LIVE_PERFORMANCE_FIELDS;

/** A staged work (play, musical, opera, dance) or a performer-led concert. */
export type PerformanceKind = "staged" | "concert";

export type CreditAs = "by" | "musicBy" | "musicAndLyricsBy";

const CREDIT_PHRASES: Readonly<Record<CreditAs, string>> = {
  by: "by",
  musicBy: "music by",
  musicAndLyricsBy: "music and lyrics by",
};

export interface LivePerformanceLabels extends MediaLabels {
  mention: string;
  apaNote: string;
  mlaNote: string;
  chicagoNote: string;
}

interface Performance {
  kind: PerformanceKind;
  title: string;
  creators: PersonName[];
  credit: string;
  director: string;
  conductor: string;
  choreographer: string;
  performers: PersonName[];
  company: string;
  performer: PersonName | null;
  venue: string;
  city: string;
  region: string;
  date: PartialDate | null;
}

function readPerformer(values: MediaValues): PersonName | null {
  const name = get(values, F.performer);
  if (name === "") {
    return null;
  }
  return get(values, F.performerIsGroup) === "true"
    ? groupName(name)
    : parsePersonName(name);
}

function readPerformance(
  kind: PerformanceKind,
  values: MediaValues
): Performance {
  const credit = (get(values, F.creditAs) || "by") as CreditAs;
  return {
    kind,
    title: capitalizeSubtitle(get(values, F.title)),
    creators: parsePeople(get(values, F.creators)),
    credit: CREDIT_PHRASES[credit] ?? CREDIT_PHRASES.by,
    director: get(values, F.director),
    conductor: get(values, F.conductor),
    choreographer: get(values, F.choreographer),
    performers: parsePeople(get(values, F.performers)),
    company: get(values, F.company),
    performer: kind === "concert" ? readPerformer(values) : null,
    venue: get(values, F.venue),
    city: get(values, F.city),
    region: get(values, F.region),
    date: parsePartialDate(get(values, F.date)),
  };
}

function capitalizeFirst(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** The roles after a staged work's title, in MLA's and Chicago's shared order. */
function roleCredits(show: Performance, performersWord: string): string[] {
  const credits: string[] = [];
  if (show.creators.length > 0) {
    credits.push(
      `${show.credit} ${mlaRoleNames(show.creators.map(naturalName))}`
    );
  }
  if (show.director) {
    credits.push(`directed by ${show.director}`);
  }
  if (show.conductor) {
    credits.push(`conducted by ${show.conductor}`);
  }
  if (show.choreographer) {
    credits.push(`choreographed by ${show.choreographer}`);
  }
  if (show.performers.length > 0) {
    const names = mlaRoleNames(show.performers.map(naturalName));
    credits.push(`${performersWord} ${names}`);
  }
  return credits;
}

/* ---------------------------------------------------------------------- APA */

function apaMention(show: Performance): CitationParts {
  const place = [
    show.venue,
    show.city,
    show.region,
    longDate(show.date),
  ].filter(Boolean);
  const where = place.length > 0 ? ` (${place.join(", ")})` : "";
  if (show.kind === "concert") {
    const performer = show.performer ? naturalName(show.performer) : "";
    const inside = [show.title, ...place].filter(Boolean).join(", ");
    return [plain(`${performer}${inside ? ` (${inside})` : ""}`)];
  }
  return [italic(titleCase(show.title)), plain(where)];
}

function apaOutput(
  show: Performance,
  labels: LivePerformanceLabels
): CitationOutput {
  return {
    key: "apa",
    label: labels.apa,
    entries: [{ label: labels.mention, parts: apaMention(show) }],
    note: labels.apaNote,
  };
}

/* ---------------------------------------------------------------------- MLA */

function mlaStaged(show: Performance): CitationParts {
  const title = titleCase(show.title);
  const performersWord =
    show.performers.length > 1 ? "performances by" : "performance by";
  const elements = [
    ...roleCredits(show, performersWord),
    show.company,
    mlaDate(show.date),
    show.venue,
    show.city,
  ].filter(Boolean);
  return tidyParts([
    italic(title),
    plain(closer(title)),
    plain(` ${withPeriod(capitalizeFirst(elements.join(", ")))}`),
  ]);
}

function mlaConcert(show: Performance): CitationParts {
  const performer = show.performer ? invertedName(show.performer) : "";
  const title = show.title || "Concert";
  const elements = [
    show.conductor ? `conducted by ${show.conductor}` : "",
    mlaDate(show.date),
    show.venue,
    show.city,
  ].filter(Boolean);
  return tidyParts([
    plain(performer ? `${performer}${closer(performer)} ` : ""),
    plain(`${title}${closer(title)} `),
    plain(withPeriod(capitalizeFirst(elements.join(", ")))),
  ]);
}

function mlaInText(show: Performance): CitationParts {
  if (show.kind === "concert" && show.performer) {
    return [plain(`(${familyName(show.performer)})`)];
  }
  return [plain("("), italic(mlaShortTitle(titleCase(show.title))), plain(")")];
}

function mlaOutput(
  show: Performance,
  labels: LivePerformanceLabels
): CitationOutput {
  return {
    key: "mla",
    label: labels.mla,
    entries: [
      {
        label: labels.worksCited,
        parts: show.kind === "concert" ? mlaConcert(show) : mlaStaged(show),
      },
      { label: labels.inText, parts: mlaInText(show) },
    ],
    note: labels.mlaNote,
  };
}

/* ------------------------------------------------------------------ Chicago */

function chicagoStagedNote(show: Performance): CitationParts {
  const elements = [
    ...roleCredits(show, "featuring"),
    show.company,
    show.venue,
    show.city,
    longDate(show.date),
  ].filter(Boolean);
  return tidyParts([
    italic(titleCase(show.title)),
    plain(elements.length > 0 ? `, ${withPeriod(elements.join(", "))}` : "."),
  ]);
}

function chicagoConcertNote(show: Performance): CitationParts {
  const elements = [
    show.performer ? naturalName(show.performer) : "",
    show.title || "concert",
    show.conductor ? `conducted by ${show.conductor}` : "",
    show.venue,
    show.city,
    longDate(show.date),
  ].filter(Boolean);
  return [plain(withPeriod(elements.join(", ")))];
}

function chicagoShortNote(show: Performance): CitationParts {
  if (show.kind === "concert") {
    const performer = show.performer ? familyName(show.performer) : "";
    const short = [performer, show.title].filter(Boolean);
    return [plain(withPeriod(short.join(", ")))];
  }
  return [italic(chicagoShortTitle(titleCase(show.title))), plain(".")];
}

function chicagoOutput(
  show: Performance,
  labels: LivePerformanceLabels
): CitationOutput {
  return {
    key: "chicago",
    label: labels.chicago,
    entries: [
      {
        label: labels.footnote,
        parts:
          show.kind === "concert"
            ? chicagoConcertNote(show)
            : chicagoStagedNote(show),
      },
      { label: labels.shortNote, parts: chicagoShortNote(show) },
    ],
    note: labels.chicagoNote,
  };
}

export function stagedPerformanceIsReady(values: MediaValues): boolean {
  return (
    get(values, F.title) !== "" &&
    parsePartialDate(get(values, F.date)) !== null
  );
}

export function concertIsReady(values: MediaValues): boolean {
  return (
    get(values, F.performer) !== "" &&
    parsePartialDate(get(values, F.date)) !== null
  );
}

export function formatLivePerformance(
  kind: PerformanceKind,
  values: MediaValues,
  labels: LivePerformanceLabels
): CitationOutput[] {
  const show = readPerformance(kind, values);
  return [
    apaOutput(show, labels),
    mlaOutput(show, labels),
    chicagoOutput(show, labels),
  ];
}
